import {
  IAB_PREDICATE_KEY,
  NON_SELECT_FLAG_TYPES,
  selectStoredExposures,
} from "./constants";
import { usePricing } from "components/customPricing/iabModel1/pricingHooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as modelDataStore from "store/modelDataStore";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import {
  selectActiveLayerIndex,
  selectResolvedActiveLayer,
} from "store/selectors/pricing/pricingSelectors";

const isIabFlag = (flag) => flag.predicate?.[IAB_PREDICATE_KEY] === true;

const buildSelectFlags = (
  exposureFlags,
  configRatingFactorOverrides,
  lastRunConfig
) => {
  const base = exposureFlags
    .filter(
      (f) =>
        f.categoryType === "select" &&
        f.type &&
        !NON_SELECT_FLAG_TYPES.includes(f.type) &&
        isIabFlag(f)
    )
    .map((flag) => {
      const overrideCategories = configRatingFactorOverrides[flag.type] ?? {};
      const existingKeys = new Set(
        (flag.categoryOptions ?? []).map((o) => o.key)
      );
      const extraOptions = Object.keys(overrideCategories)
        .filter((k) => !existingKeys.has(k))
        .map((k) => ({ key: k, display: k }));
      return extraOptions.length > 0
        ? {
            ...flag,
            categoryOptions: [...(flag.categoryOptions ?? []), ...extraOptions],
          }
        : flag;
    });

  const knownTypes = new Set(base.map((f) => f.type));
  const customFlags = Object.entries(configRatingFactorOverrides)
    .filter(([key]) => !knownTypes.has(key))
    .map(([key, categories]) => ({
      type: key,
      displayName: key,
      categoryType: "select",
      categoryOptions: Object.keys(categories).map((k) => ({
        key: k,
        display: k,
      })),
    }));

  const allKnownTypes = new Set([
    ...knownTypes,
    ...customFlags.map((f) => f.type),
  ]);
  const lastRunFactors = lastRunConfig?.ratingFactors ?? {};
  const lastRunDisplayNames = lastRunConfig?.displayNameMap ?? {};
  const legacyFlags = Object.entries(lastRunFactors)
    .filter(([key]) => !allKnownTypes.has(key))
    .map(([key, categories]) => ({
      type: key,
      displayName: lastRunDisplayNames[key] ?? key,
      categoryType: "select",
      categoryOptions: Object.keys(categories).map((k) => ({
        key: k,
        display: k,
      })),
      isLegacy: true,
    }));

  // Preserve ordering from last run, append new factors at the end
  const allFlagsMap = new Map(
    [...base, ...customFlags, ...legacyFlags].map((f) => [f.type, f])
  );
  const lastRunOrder = Object.keys(lastRunFactors);
  const ordered = [];
  const seen = new Set();
  for (const type of lastRunOrder) {
    if (allFlagsMap.has(type)) {
      ordered.push(allFlagsMap.get(type));
      seen.add(type);
    }
  }
  for (const [type, flag] of allFlagsMap) {
    if (!seen.has(type)) ordered.push(flag);
  }
  return ordered.length > 0 ? ordered : [...base, ...customFlags];
};

const buildBaseRatingFactors = (selectFlags) => {
  const result = {};
  for (const flag of selectFlags) {
    const entries = (flag.categoryOptions ?? []).filter((o) => o.value != null);
    if (entries.length > 0) {
      result[flag.type] = Object.fromEntries(
        entries.map((o) => [o.key, o.value])
      );
    }
  }
  return result;
};

const computeConfigDiff = (
  lastRunConfig,
  baseRatingFactors,
  values,
  displayNameMap
) => {
  const lastRunFactors = lastRunConfig?.ratingFactors ?? {};
  const lastRunDisplayNames = lastRunConfig?.displayNameMap ?? {};
  const lastRunResults = values?.ratingFactors ?? {};
  if (!lastRunConfig || Object.keys(baseRatingFactors).length === 0) return [];

  const allTypes = new Set([
    ...Object.keys(lastRunFactors),
    ...Object.keys(baseRatingFactors),
  ]);
  const diffs = [];

  for (const type of allTypes) {
    const displayName =
      lastRunDisplayNames[type] ?? displayNameMap[type] ?? type;
    const oldCategories = lastRunFactors[type];
    const newCategories = baseRatingFactors[type];

    if (!newCategories) {
      const usedEntry = lastRunResults[type];
      diffs.push({
        type,
        displayName,
        category: usedEntry?.category ?? "—",
        lastFactor: usedEntry?.factor ?? null,
        currentFactor: null,
        kind: "factor_removed",
      });
      continue;
    }

    if (!oldCategories) {
      diffs.push({
        type,
        displayName,
        category: "—",
        lastFactor: null,
        currentFactor: null,
        kind: "factor_added",
      });
      continue;
    }

    const allCats = new Set([
      ...Object.keys(oldCategories),
      ...Object.keys(newCategories),
    ]);
    for (const cat of allCats) {
      const oldVal = oldCategories[cat];
      const newVal = newCategories[cat];
      if (oldVal == null) {
        diffs.push({
          type,
          displayName,
          category: cat,
          lastFactor: null,
          currentFactor: newVal,
          kind: "field_added",
        });
      } else if (newVal == null) {
        diffs.push({
          type,
          displayName,
          category: cat,
          lastFactor: oldVal,
          currentFactor: null,
          kind: "category_removed",
        });
      } else if (Math.abs(newVal - oldVal) > 1e-9) {
        diffs.push({
          type,
          displayName,
          category: cat,
          lastFactor: oldVal,
          currentFactor: newVal,
          kind: "value_changed",
        });
      }
    }
  }
  return diffs;
};

export const useIabPricingState = () => {
  const dispatch = useDispatch();
  const {
    data,
    config: engineConfig,
    isFetching,
    layers,
    refetch,
    iabConfig,
    applyLatestConfig,
  } = usePricing();

  const { data: flagsConfig } = staticDataService.useConfigQuery("flags", {
    refetchOnMountOrArgChange: true,
  });
  const exposureFlags = useMemo(() => flagsConfig?.defaultExposureFlags ?? [], [
    flagsConfig,
  ]);
  const revenueBandFlag = exposureFlags.find((f) => f.type === "REVENUE_BAND");
  const manualModifierFlag = exposureFlags.find(
    (f) => f.type === "MANUAL_MODIFIER"
  );

  const configRatingFactorOverrides = useMemo(
    () => iabConfig?.ratingFactors ?? {},
    [iabConfig]
  );
  const lastRunConfig = data?.lastRunConfig;

  const selectFlags = buildSelectFlags(
    exposureFlags,
    configRatingFactorOverrides,
    lastRunConfig
  );

  const displayNameMap = useMemo(
    () =>
      Object.fromEntries(
        exposureFlags.filter((f) => f.type).map((f) => [f.type, f.displayName])
      ),
    [exposureFlags]
  );

  const baseRatingFactors = useMemo(() => buildBaseRatingFactors(selectFlags), [
    selectFlags,
  ]);

  // Selections state
  const storedExposures = useSelector(selectStoredExposures);
  const [selections, setSelections] = useState(() =>
    Object.fromEntries(
      storedExposures
        .filter((f) => f.type && f.category != null && f.category !== "")
        .map((f) => [f.type, f.category])
    )
  );
  const lastManualModifierRef = useRef(
    storedExposures.find((f) => f.type === "MANUAL_MODIFIER")?.category
  );

  const [invalidSelections, setInvalidSelections] = useState(new Set());

  // Factor modal state
  const [factorModalOpen, setFactorModalOpen] = useState(false);
  const [factorDraft, setFactorDraft] = useState({});
  const [factorCustomKeys, setFactorCustomKeys] = useState([]);

  const handleChange = useCallback((type, value) => {
    setSelections((prev) => ({ ...prev, [type]: value }));
    setInvalidSelections((prev) => {
      if (!prev.has(type)) return prev;
      const next = new Set(prev);
      next.delete(type);
      return next;
    });
  }, []);

  const openFactorModal = useCallback(() => {
    setFactorDraft(configRatingFactorOverrides);
    setFactorCustomKeys(
      Object.keys(configRatingFactorOverrides).filter(
        (k) => !selectFlags.some((f) => f.type === k)
      )
    );
    setFactorModalOpen(true);
  }, [configRatingFactorOverrides, selectFlags]);

  const handleApplyFactorOverrides = useCallback(
    (overrides) => {
      dispatch(
        modelDataStore.update({
          iabConfig: { ...iabConfig, ratingFactors: overrides },
        })
      );
      // Auto-select first option for newly added custom factor types
      const knownTypes = new Set(selectFlags.map((f) => f.type));
      setSelections((prev) => {
        const additions = {};
        for (const [key, categories] of Object.entries(overrides)) {
          if (!knownTypes.has(key) && !prev[key]) {
            const first = Object.keys(categories)[0];
            if (first) additions[key] = first;
          }
        }
        return Object.keys(additions).length > 0
          ? { ...prev, ...additions }
          : prev;
      });
      setFactorModalOpen(false);
    },
    [dispatch, iabConfig, selectFlags]
  );

  // Revenue
  const exposureData = useSelector(exposureSelectors.selectExposureData);
  const latestRevenue = useMemo(() => {
    const years = Object.keys(exposureData ?? {})
      .map(Number)
      .sort((a, b) => b - a);
    for (const year of years) {
      const val = exposureData[String(year)]?.REVENUE;
      if (val != null) return val;
    }
    return null;
  }, [exposureData]);

  // Auto-select first options
  useEffect(() => {
    setSelections((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const flag of selectFlags) {
        if (next[flag.type]) continue;
        const firstOption = flag.categoryOptions?.[0]?.key;
        if (firstOption != null) {
          next[flag.type] = firstOption;
          changed = true;
        }
      }
      if (!next.REVENUE_BAND) {
        const options = revenueBandFlag?.categoryOptions ?? [];
        if (options.length > 0) {
          if (latestRevenue != null) {
            const sorted = [...options].sort(
              (a, b) => parseFloat(a.key) - parseFloat(b.key)
            );
            const floorOption =
              sorted
                .filter((opt) => parseFloat(opt.key) <= latestRevenue)
                .pop() ?? sorted[0];
            next.REVENUE_BAND = floorOption.key;
          } else {
            next.REVENUE_BAND = options[0].key;
          }
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [latestRevenue, revenueBandFlag, selectFlags]);

  // Debounced manual modifier auto-recalc
  useEffect(() => {
    if (lastManualModifierRef.current === selections.MANUAL_MODIFIER) return;
    lastManualModifierRef.current = selections.MANUAL_MODIFIER;
    const timeoutId = setTimeout(() => {
      refetch(selections, baseRatingFactors, displayNameMap);
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [
    refetch,
    selections,
    selections.MANUAL_MODIFIER,
    baseRatingFactors,
    displayNameMap,
  ]);

  const activeLayer = useSelector(selectResolvedActiveLayer);
  const activeLayerIndex = useSelector(selectActiveLayerIndex) ?? 0;
  const allLayerResults = data?.layers ?? {};
  const values = allLayerResults[activeLayerIndex]?.values;
  const failure = allLayerResults[activeLayerIndex]?.failure;

  const configDiff = useMemo(
    () =>
      computeConfigDiff(
        lastRunConfig,
        baseRatingFactors,
        values,
        displayNameMap
      ),
    [lastRunConfig, baseRatingFactors, values, displayNameMap]
  );

  const handleApplyLatestConfig = useCallback(() => {
    const nowInvalid = new Set();
    for (const [type, category] of Object.entries(selections)) {
      if (
        category &&
        type in baseRatingFactors &&
        !(category in baseRatingFactors[type])
      ) {
        nowInvalid.add(type);
      }
    }
    setInvalidSelections(nowInvalid);

    const legacyTypes = selectFlags
      .filter((f) => f.isLegacy)
      .map((f) => f.type);
    if (legacyTypes.length > 0) {
      setSelections((prev) => {
        const next = { ...prev };
        for (const type of legacyTypes) delete next[type];
        return next;
      });
    }
    // Re-order baseRatingFactors to match the default config (exposureFlags) array order
    const orderedBaseRatingFactors = Object.fromEntries(
      exposureFlags
        .filter((f) => f.type && baseRatingFactors[f.type])
        .map((f) => [f.type, baseRatingFactors[f.type]])
    );
    applyLatestConfig(orderedBaseRatingFactors, displayNameMap);
  }, [
    baseRatingFactors,
    selections,
    selectFlags,
    applyLatestConfig,
    displayNameMap,
    exposureFlags,
  ]);

  const recalculate = useCallback(
    () => refetch(selections, baseRatingFactors, displayNameMap),
    [refetch, selections, baseRatingFactors, displayNameMap]
  );

  return {
    data,
    engineConfig,
    isFetching,
    layers,
    activeLayer,
    allLayerResults,
    values,
    failure,
    selectFlags,
    selections,
    invalidSelections,
    handleChange,
    recalculate,
    revenueBandFlag,
    manualModifierFlag,
    latestRevenue,
    configDiff,
    handleApplyLatestConfig,
    factorModalOpen,
    setFactorModalOpen,
    openFactorModal,
    factorDraft,
    setFactorDraft,
    factorCustomKeys,
    setFactorCustomKeys,
    handleApplyFactorOverrides,
    baseRatingFactors,
    displayNameMap,
  };
};
